export interface ExportCol<T> {
  key: keyof T;
  header: string;
}

// xlsx is heavy (~hundreds of KB); load it on demand so it stays out of the main bundle.
const getXLSX = () => import('xlsx');

export function toSheetRows<T>(
  rows: T[],
  cols: ExportCol<T>[],
): Record<string, unknown>[] {
  return rows.map((r) =>
    Object.fromEntries(cols.map((c) => [c.header, r[c.key]])),
  );
}
export async function exportExcel<T>(
  filename: string,
  rows: T[],
  cols: ExportCol<T>[],
): Promise<void> {
  const XLSX = await getXLSX();
  const ws = XLSX.utils.json_to_sheet(toSheetRows(rows, cols));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
export async function exportCsv<T>(
  filename: string,
  rows: T[],
  cols: ExportCol<T>[],
): Promise<void> {
  const XLSX = await getXLSX();
  const ws = XLSX.utils.json_to_sheet(toSheetRows(rows, cols));
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${filename}.csv`);
}
export function buildPdfDocDef<T>(
  title: string,
  rows: T[],
  cols: ExportCol<T>[],
) {
  return {
    content: [
      { text: title, style: 'header' },
      {
        table: {
          headerRows: 1,
          body: [
            cols.map((c) => c.header),
            ...rows.map((r) => cols.map((c) => String(r[c.key]))),
          ],
        },
      },
    ],
    styles: { header: { fontSize: 16, bold: true, margin: [0, 0, 0, 8] } },
  };
}
async function getPdfMake() {
  // pdfmake's build subpath/vfs shape varies by version; typed loosely on purpose
  // (only exercised in the manual browser path — Thai font embedding is Slice 2).
  const pdfMake: any = (await import('pdfmake/build/pdfmake')).default;
  const pdfFonts: any = (await import('pdfmake/build/vfs_fonts')).default;
  pdfMake.vfs = pdfFonts.vfs ?? pdfFonts.pdfMake?.vfs;
  return pdfMake;
}
export async function buildPdfBlob<T>(
  title: string,
  rows: T[],
  cols: ExportCol<T>[],
): Promise<Blob> {
  const pdfMake = await getPdfMake();
  return new Promise((resolve) =>
    pdfMake.createPdf(buildPdfDocDef(title, rows, cols)).getBlob(resolve),
  );
}
export async function exportPdf<T>(
  filename: string,
  title: string,
  rows: T[],
  cols: ExportCol<T>[],
): Promise<void> {
  triggerDownload(await buildPdfBlob(title, rows, cols), `${filename}.pdf`);
}
function triggerDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
